import {createSlice, isAnyOf} from '@reduxjs/toolkit'
import type {MemberAccess} from '~/src/models/memberAccess'
import type {TRootState} from '~/src/store'
import type {
  ICompanyTypeMaps,
  IRole,
  IUserBase as TFullUser,
  TPartialUser,
  TUserAttributes,
} from '../../models'
import {BackendRolePermissions} from '../../utils/permissions'
import {authApi} from '../apis/auth-api'
import {userApi} from '../apis/user-api'
// Cognito thunks removed - now using MSP platform JWT authentication
import {memberAccessApi} from './../apis/member-access-api'

export interface IRootState {
  userAttributes?: TUserAttributes
  oldPassword?: string
  redirectTo: string | null
  partialUser: TPartialUser | null
  currentUser: TFullUser | null
  onSignIn?: boolean
  role: IRole | null
  token: string | null
  refreshToken: string | null
  appIsLoaded: boolean
  loadingUser?: boolean
  companyTypeMaps: ICompanyTypeMaps | null
  loggedInAgencyId: string
  loggedInCompanyId: string
  gaMemberAccess?: MemberAccess
  // Backend role permissions for unified permission checking
  backendRolePermissions: BackendRolePermissions | null
  // Flattened permission strings for quick lookup (e.g., 'Company.Create')
  flattenedPermissions: string[]
}

const tempRole = window.localStorage.getItem('cognitoRole') ?? 'null'

const initialState: IRootState = {
  appIsLoaded: false,
  currentUser: null,
  loadingUser: true,
  partialUser: null,
  redirectTo: null,
  token: null,
  refreshToken: null,
  companyTypeMaps: null,
  onSignIn: false,
  loggedInAgencyId: '',
  loggedInCompanyId: '',
  // !important we don't actually want to do this. Remove after demo.
  role: JSON.parse(tempRole),
  // Backend role permissions (populated after login)
  backendRolePermissions: null,
  flattenedPermissions: [],
}

const rootSlice = createSlice({
  name: 'rootSlice',
  initialState,
  reducers: {
    redirect: (state) => {
      state.redirectTo = null
    },
    updateUserSignIn: (state, {payload}) => {
      const {data, index} = payload
      state.onSignIn = data
    },
    updateLoggedInAgencyId: (state, {payload}) => {
      state.loggedInAgencyId = payload
    },
    updateLoggedInCompanyId: (state, {payload}) => {
      state.loggedInCompanyId = payload
    },
    updateCurrentUser: (state, {payload}) => {
      state.currentUser = payload
    },
    setAppLoaded: (state, {payload}) => {
      state.appIsLoaded = payload
    },
    /**
     * Set backend role permissions from API response
     * Also flattens permissions for quick lookup
     */
    setBackendRolePermissions: (state, {payload}: {payload: BackendRolePermissions | null}) => {
      state.backendRolePermissions = payload
      // Flatten permissions for quick lookup: ['Company.Create', 'Company.Read', etc.]
      if (payload?.accessList) {
        state.flattenedPermissions = payload.accessList.flatMap((item) =>
          item.operations.map((op) => `${item.entityType}.${op}`),
        )
      } else {
        state.flattenedPermissions = []
      }
    },
    /**
     * Clear permissions on logout
     */
    clearBackendRolePermissions: (state) => {
      state.backendRolePermissions = null
      state.flattenedPermissions = []
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        isAnyOf(authApi.endpoints.signin.matchFulfilled, authApi.endpoints.iopLogin.matchFulfilled),
        (state, {payload}) => {
          // Handle legacy Cognito response format
          if ('oldPassword' in payload) {
            const {oldPassword, ...rest} = payload
            state.userAttributes = rest
            state.oldPassword = oldPassword
          }

          // Handle legacy token format
          if ('token' in payload) {
            state.token = payload.token
          }

          // Handle MSP / IOP authentication response
          if ('accessToken' in payload) {
            state.token = payload.idToken
            state.refreshToken = payload.refreshToken || null
          }

          // Handle token-based authentication (from URL redirect)
          if (
            'isSuccess' in payload &&
            payload.isSuccess &&
            payload.message === 'Token authentication successful'
          ) {
            state.token = payload.idToken
          }
        },
      )
      .addMatcher(authApi.endpoints.signout.matchFulfilled, (state) => {
        state.currentUser = null
        state.refreshToken = null
      })
      .addMatcher(authApi.endpoints.refreshSession.matchFulfilled, (state, {payload}) => {
        if (payload.idToken) {
          state.token = payload.idToken
        }
        if (payload.refreshToken) {
          state.refreshToken = payload.refreshToken
        }
      })
      // Removed getPartialUser rejected matcher - using MSP platform auth
      .addMatcher(userApi.endpoints.getUserByEmail.matchFulfilled, (state, {payload}) => {
        state.appIsLoaded = true
      })
      .addMatcher(userApi.endpoints.getUserByEmail.matchRejected, (state) => {
        state.appIsLoaded = true
      })
      // Legacy roleApi.endpoints.getRole.matchFulfilled removed - now using roleApi service
      .addMatcher(userApi.endpoints.getUserByEmail.matchPending, (state) => {
        state.loadingUser = true
      })
      .addMatcher(
        isAnyOf(
          userApi.endpoints.getUserByEmail.matchFulfilled,
          userApi.endpoints.getUserByEmail.matchRejected,
        ),
        (state) => {
          state.loadingUser = false
        },
      )
      .addMatcher(
        memberAccessApi.endpoints.getMemberAccessByMemberId.matchFulfilled,
        (state, {payload}) => {
          state.gaMemberAccess = payload
        },
      )
  },
})

export const {
  redirect,
  updateUserSignIn,
  updateLoggedInAgencyId,
  updateLoggedInCompanyId,
  updateCurrentUser,
  setAppLoaded,
  setBackendRolePermissions,
  clearBackendRolePermissions,
} = rootSlice.actions
export default rootSlice.reducer
export const selectRoot = (state: TRootState) => state.root

// Selectors for permissions
export const selectBackendRolePermissions = (state: TRootState) => state.root.backendRolePermissions
export const selectFlattenedPermissions = (state: TRootState) => state.root.flattenedPermissions

/**
 * Selector to check if user has a specific permission
 * @param permission - Permission string in format 'EntityType.Operation' (e.g., 'Company.Create')
 */
export const selectHasPermission = (permission: string) => (state: TRootState) =>
  state.root.flattenedPermissions.includes(permission)
